import { ApolloClient, gql, NormalizedCacheObject, useQuery } from "@apollo/client";
import { AgentAggregateScore, Query } from "../types/generated/graphql";
import DataLoader from 'dataloader';

// GraphQL query to get aggregate score for a video
export const QUERY_GET_AGGREGATE_SCORE = gql`
  query getAggregateScoreForVideo($videoId: String!, $childAge: Int) {
    getAggregateScoreForVideo(videoId: $videoId, childAge: $childAge) {
      videoId
      score
      rating
      restricted
      explanation
      restrictingAgents {
        specialization
        version
      }
      overriddenAgents {
        specialization
        version
      }
    }
  }
`;

// GraphQL query to get aggregate scores for multiple videos
export const QUERY_GET_AGGREGATE_SCORES = gql`
  query getAggregateScoreForVideos($videoIds: [String!]!, $childAge: Int) {
    getAggregateScoreForVideos(videoIds: $videoIds, childAge: $childAge) {
      videoId
      score
      rating
      restricted
      explanation
      restrictingAgents {
        specialization
        version
      }
      overriddenAgents {
        specialization
        version
      }
    }
  }
`;

// Hook for using the query
export const useGetAggregateScore = (
  client: ApolloClient<NormalizedCacheObject>,
  videoId: string,
  childAge?: number | null,
) => {
  type T = Pick<Query, 'getAggregateScoreForVideo'>;

  const { data, loading, error } = useQuery<T>(QUERY_GET_AGGREGATE_SCORE, {
    client,
    variables: { videoId, childAge: childAge ?? null },
    fetchPolicy: "network-only",
  });

  return { data, loading, error };
};

// DataLoader for batching multiple video score requests. Keyed on the
// Apollo client + `childAge` so admin age-band filtering reuses a
// separate loader per band (each with its own batch window + cache)
// instead of mixing band-specific results into the "all ages" loader.
const createAggregateScoreLoader = (
  client: ApolloClient<NormalizedCacheObject>,
  childAge: number | null,
) => {
  return new DataLoader<string, AgentAggregateScore | null>(async (keys) => {
    const videoIds = Array.from(keys);

    type U = Pick<Query, 'getAggregateScoreForVideos'>;
    try {
      const result = await client.query<U>({
        query: QUERY_GET_AGGREGATE_SCORES,
        variables: { videoIds, childAge },
        fetchPolicy: "network-only",
      });

      const scores = result.data?.getAggregateScoreForVideos || [];

      const scoreMap = new Map<string, AgentAggregateScore>();
      scores.forEach(score => {
        if (score?.videoId) {
          scoreMap.set(score.videoId, score);
        }
      });

      return keys.map(key => scoreMap.get(key) || null);
    } catch (error) {
      console.error('Error fetching aggregate scores:', error);
      return keys.map(() => null);
    }
  }, {
    maxBatchSize: 50
  });
};

// Memoize per (client, childAge) pair. `lodash.memoize` only keys on
// the first argument, so we wrap to build a composite key. Each unique
// age selection gets its own cached loader — flipping the dropdown in
// AdminVideos produces a fresh batch window for the new band.
const _loaderCache = new WeakMap<ApolloClient<NormalizedCacheObject>, Map<string, ReturnType<typeof createAggregateScoreLoader>>>();
export const getAggregateScoreLoader = (
  client: ApolloClient<NormalizedCacheObject>,
  childAge: number | null = null,
) => {
  let perClient = _loaderCache.get(client);
  if (!perClient) {
    perClient = new Map();
    _loaderCache.set(client, perClient);
  }
  const key = childAge == null ? 'all' : String(childAge);
  let loader = perClient.get(key);
  if (!loader) {
    loader = createAggregateScoreLoader(client, childAge);
    perClient.set(key, loader);
  }
  return loader;
};